import filterOutOrderProductOptions from './orderProductOptions';
import { filterOutSleeveOptions } from './sleeves';

/**
 * Box PDP selection progress: required = sum of minOptions; selected = required picks only
 * (mirrors BoxProduct countOptionalCost flattenedBox / productCount).
 */
export const getBoxSelectionProgress = (options = [], flattenedBox = []) => {
  const filtered = filterOutOrderProductOptions(options);
  const requiredCount = filtered.reduce(
    (total, option) => total + (option.minOptions || 0),
    0
  );
  const rawSelected = Array.isArray(flattenedBox) ? flattenedBox.length : 0;
  const selectedCount =
    requiredCount > 0 ? Math.min(rawSelected, requiredCount) : rawSelected;

  return {
    selectedCount,
    requiredCount,
    isComplete: requiredCount > 0 && rawSelected >= requiredCount,
  };
};

/**
 * Shared Customize PDP checkout enablement (CTA label + disabled state).
 * Min met (>=), max not exceeded, SIZE_SELECT exactly one when selected.
 */
export const isCustomizeCheckoutEnabled = (
  options = [],
  customizeProducts = null
) => {
  const filtered = filterOutSleeveOptions(options) || [];

  if (!filtered.length) {
    return true;
  }

  return filtered.every(group => {
    const picks =
      (customizeProducts && customizeProducts[group.id]) || [];
    const groupType = group.optionGroupType || group.OptionGroupType;
    const minOptions = group.minOptions || 0;
    const {maxOptions} = group;

    if (groupType === 'SIZE_SELECT') {
      if (minOptions > 0 && picks.length === 0) {
        return false;
      }
      if (picks.length > 0 && picks.length !== 1) {
        return false;
      }
      return true;
    }

    if (minOptions > 0 && picks.length < minOptions) {
      return false;
    }

    if (
      maxOptions != null &&
      maxOptions > 0 &&
      picks.length > maxOptions
    ) {
      return false;
    }

    return true;
  });
};

/**
 * Customize PDP selection progress across required option groups.
 * Uses the same sleeve filter as CustomizeProduct ProductOptions / isCheckoutEnabled
 * (cake-image groups stay if present on that PDP — unlike Box, which uses
 * filterOutOrderProductOptions and drops them for a later Order Product Options step).
 */
export const getCustomizeSelectionProgress = (
  options = [],
  customizeProducts = null
) => {
  const filtered = filterOutSleeveOptions(options) || [];
  let requiredCount = 0;
  let selectedCount = 0;

  filtered.forEach(group => {
    const minOptions = group.minOptions || 0;
    if (minOptions <= 0) {
      return;
    }

    requiredCount += minOptions;
    const picks =
      (customizeProducts && customizeProducts[group.id]) || [];
    selectedCount += Math.min(picks.length, minOptions);
  });

  const isCheckoutEnabled = isCustomizeCheckoutEnabled(
    options,
    customizeProducts
  );

  return {
    selectedCount,
    requiredCount,
    // Align with CTA disable: enough picks and within max / SIZE rules
    isComplete:
      requiredCount > 0 && selectedCount >= requiredCount && isCheckoutEnabled,
    isCheckoutEnabled,
  };
};

/**
 * Figma copy: "12-Pack (1/12 Selected)" — name Medium, count Bold in the UI.
 */
export const formatPdpSelectionLabel = (
  productName,
  selectedCount,
  requiredCount
) => {
  const safeRequired = Math.max(0, requiredCount || 0);
  const safeSelectedRaw = Math.max(0, selectedCount || 0);
  const safeSelected =
    safeRequired > 0 ? Math.min(safeSelectedRaw, safeRequired) : safeSelectedRaw;
  return {
    productName: productName || '',
    selectionText: `(${safeSelected}/${safeRequired} Selected)`,
  };
};
